import { ReactNode } from 'react'
import { Toaster } from 'react-hot-toast'
import toast from 'react-hot-toast'

interface NotificationProviderProps {
  children: ReactNode
}

// Store for tracking active error toasts to prevent duplicate toast cascades
const activeErrorToasts = new Map<string, string>()

/**
 * Creates a deduplicator for toast notifications.
 * Prevents duplicate error toast cascades by tracking error hashes.
 */
export function createToastDeduplicator() {
  return {
    error: (message: string, options?: any) => {
      // Create a hash of the error message for deduplication
      const errorHash = message.substring(0, 50) // Use first 50 chars as hash

      // If we already have a toast for this error, just return
      if (activeErrorToasts.has(errorHash)) {
        return activeErrorToasts.get(errorHash)
      }

      // Show the error toast and store its ID
      const toastId = toast.error(message, {
        ...options,
        onDismiss: () => {
          activeErrorToasts.delete(errorHash)
        },
      })

      activeErrorToasts.set(errorHash, toastId)
      return toastId
    },
    success: (message: string, options?: any) => toast.success(message, options),
    loading: (message: string, options?: any) => toast.loading(message, options),
    promise: (promise: Promise<any>, msgs: any, options?: any) =>
      toast.promise(promise, msgs, options),
  }
}

export default function NotificationProvider({
  children,
}: NotificationProviderProps) {
  return (
    <>
      {children}
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: 4000,
          className: 'am-toast',
          style: {
            // Tokens OS are HSL components (--card: 0 0% 100%), not --color-*
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '1rem',
            padding: '1rem',
            boxShadow: 'none',
            fontSize: '0.875rem',
            opacity: 1,
          },
          success: {
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              borderColor: 'hsl(var(--border))',
              borderLeft: '4px solid hsl(var(--success))',
            },
            iconTheme: {
              primary: 'hsl(var(--success))',
              secondary: 'hsl(var(--card))',
            },
          },
          error: {
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              borderColor: 'hsl(var(--border))',
              borderLeft: '4px solid hsl(var(--error))',
            },
            iconTheme: {
              primary: 'hsl(var(--error))',
              secondary: 'hsl(var(--card))',
            },
          },
          loading: {
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              borderColor: 'hsl(var(--border))',
              borderLeft: '4px solid hsl(var(--primary))',
            },
            iconTheme: {
              primary: 'hsl(var(--primary))',
              secondary: 'hsl(var(--card))',
            },
          },
        }}
      />
    </>
  )
}

export { default as toast } from 'react-hot-toast'
